import { $can } from '@pendo/permissions';
import get from 'lodash/get';
import { isExtensionApp, isWebBasedApp } from '@/utils/apps';

export function hasAppDetailsViewerAccessForAppId (id) {
    return $can(id).entity('applicationDetails').action('view');
}

export function hasAppDetailsEditorAccessForAppId (id) {
    return $can(id).entity('applicationDetails').action('edit');
}

export function hasInstallationSettingsViewerAccessForAppId (id) {
    return $can(id).entity('installationSettings').action('view');
}

export function hasInstallationSettingsEditorAccessForAppId (id) {
    return $can(id).entity('installationSettings').action('edit');
}

export function hasRawEventsViewerAccessForAppId (id) {
    return $can(id).entity('rawEvent').action('view');
}

export function hasSessionReplayViewerAccessForAppId (id) {
    return $can(id).entity('sessionReplay').action('view');
}

export function hasSessionReplayEditorAccessForAppId (id) {
    return $can(id).entity('sessionReplay').action('edit');
}

export function hasWebSdkSettingsViewerAccessForAppId (id) {
    return $can(id).entity('webSdkSettings').action('view');
}

export function hasWebSdkSettingsEditorAccessForAppId (id) {
    return $can(id).entity('webSdkSettings').action('edit');
}

export function hasLocalizationSettingsViewerAccessForAppId (id) {
    return $can(id).entity('localizationSettings').action('view');
}

export function hasLocalizationSettingsEditorAccessForAppId (id) {
    return $can(id).entity('localizationSettings').action('edit');
}

export function hasTaggingAndGuideSettingsViewerAccessForAppId (id) {
    return $can(id).entity('taggingAndGuideSettings').action('view');
}

export function hasTaggingAndGuideSettingsEditorAccessForAppId (id) {
    return $can(id).entity('taggingAndGuideSettings').action('edit');
}

export function hasCookieConsentSettingsViewerAccessForAppId (id) {
    return $can(id).entity('cookieConsentSettings').action('view');
}

export function hasCookieConsentSettingsEditorAccessForAppId (id) {
    return $can(id).entity('cookieConsentSettings').action('edit');
}

export function buildAppSettingsTabAccessContext ({
    featureAvailability = {},
    hasAdminAccess = false,
    hasSessionReplayViewerAccess = false,
    hideRawEvents = false,
    isPendoFree = false,
    isInTrial = false,
    isAdoptEndUser = false,
    enableCustomUserRoles = false,
} = {}) {
    return {
        featureAvailability,
        hasAdminAccess,
        hasSessionReplayViewerAccess,
        hideRawEvents,
        isPendoFree,
        isInTrial,
        isAdoptEndUser,
        enableCustomUserRoles,
    };
}

export function hasAppSettingsTabAccessForAppId (id, app, {
    featureAvailability = {},
    hasAdminAccess = false,
    hasSessionReplayViewerAccess = false,
    hideRawEvents = false,
    isPendoFree = false,
    isInTrial = false,
    isAdoptEndUser = false,
    enableCustomUserRoles = false,
} = {}) {
    if (!app || isAdoptEndUser) {
        return false;
    }

    const {
        hasLocalization = false,
        hasResponseRedaction = false,
        hasTaggingAndGuide = false,
    } = featureAvailability;

    const isPartnerAdoptApplication = get(app, 'applicationFlags.partnerAdoptApplication', false);
    const isMobileApp = get(app, 'applicationFlags.mobileRetroactiveAnalyticsApp', false);
    const isExtensionPlatform = isExtensionApp(app.platform);
    const isWebBasedAppPlatform = isWebBasedApp(app.platform);
    const isValidReplayPlatform = isWebBasedAppPlatform || isMobileApp;
    const effectiveHasLocalization = (isPendoFree || isInTrial)
        ? get(app, 'applicationFlags.guideLocalization', false)
        : hasLocalization;

    if (hasAppDetailsViewerAccessForAppId(id)) {
        return true;
    }

    if (hasRawEventsViewerAccessForAppId(id) && !hideRawEvents) {
        return true;
    }

    if (hasAdminAccess && !isPartnerAdoptApplication && !isMobileApp && !isExtensionPlatform) {
        return true;
    }

    if (hasInstallationSettingsViewerAccessForAppId(id) && !isPartnerAdoptApplication && !isExtensionPlatform) {
        return true;
    }

    if (
        enableCustomUserRoles
        && hasWebSdkSettingsViewerAccessForAppId(id)
        && !isPartnerAdoptApplication
        && !isMobileApp
        && !isExtensionPlatform
    ) {
        return true;
    }

    if (
        enableCustomUserRoles
        && hasLocalizationSettingsViewerAccessForAppId(id)
        && !isPartnerAdoptApplication
        && effectiveHasLocalization
    ) {
        return true;
    }

    if (hasAdminAccess && !isPartnerAdoptApplication && isMobileApp) {
        return true;
    }

    if (
        enableCustomUserRoles
        && hasTaggingAndGuideSettingsViewerAccessForAppId(id)
        && !isPartnerAdoptApplication
        && !isMobileApp
        && (hasTaggingAndGuide || hasResponseRedaction)
    ) {
        return true;
    }

    if (
        enableCustomUserRoles
        && hasCookieConsentSettingsViewerAccessForAppId(id)
        && !isPartnerAdoptApplication
        && !isMobileApp
    ) {
        return true;
    }

    const hasSessionReplayAccess = hasSessionReplayViewerAccess && isValidReplayPlatform;

    if (
        enableCustomUserRoles
        && hasSessionReplayViewerAccessForAppId(id)
        && !isPartnerAdoptApplication
        && hasSessionReplayAccess
    ) {
        return true;
    }

    return false;
}

export function hasManagementAccessForAppId (id, app, tabAccessContext) {
    return hasAppSettingsTabAccessForAppId(id, app, tabAccessContext);
}

export function hasManagementAccessForAppIds (apps, tabAccessContext) {
    if (!apps?.length) {
        return false;
    }

    return apps.some((app) => hasManagementAccessForAppId(app.id, app, tabAccessContext));
}
